import React from 'react';
import { InvoiceTransactionVO } from "../../../types/model/valueObject/invoice/InvoiceTransactionVO";
import { formatCurrencyValue } from "../../../util/PaymentUtils";
import { getServiceFeeDisplayValue } from '../../invoices/serviceFeeHelper';
import { useSnackbar } from "../../../context/snackbar-context";
import { 
  BookingTransactionType,
  BookingTransactionTypeLabel
} from "../../constants/securspace-constants";
import { BookingTransactionStatus } from '../../constants/booking-transaction-constants';
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  ActionItem
} from "../../cardComponents";
import { useMenu } from "../../../hooks/report/useMenu";
import {
  ActionTypes,
  ActionDefaultIcons,
  ActionPayload,
  CallbackActions,
  type OnActionCallback,
} from "../../../util/ActionUtils";

export interface BuyerInvoicesCardListItemProps {
  item: InvoiceTransactionVO;
  expanded?: boolean;
  onToggleExpand: () => void;
}

const OVERAGE_TYPES: string[] = [
  BookingTransactionType.OVERAGE_CHARGE,
  BookingTransactionType.DAILY_OVERAGE_CHARGE,
  BookingTransactionType.MANUAL_OVERAGE_CHARGE,
]

const openTabActionPayloads = {
  viewInvoice: {
    endpoint: (item: InvoiceTransactionVO) => `/buyer-invoice/${item.refundForTransactionNumber || item.transactionNumber}`,
    errorMessage: "Unable to view this invoice",
  },
  viewUsage: {
    endpoint: (item: InvoiceTransactionVO) => {
      let queryParams;
      if (OVERAGE_TYPES.includes(item.transactionType)) {
        queryParams = new URLSearchParams({
          overageInvoiceNumber: String(item.transactionNumber)
        }).toString();
      } else {
        queryParams = new URLSearchParams({
          //these param names match the existing spaceUsage report prior to it being modernized, so we need to keep them the same for now
          initBookingNumber: item.bookingNumber,       //be ready to change this to bookingNumber
          initStartDate: item.startDate,               //be ready to change this to startDate
          initEndDate: item.endDate                    //be ready to change this to endDate
        }).toString();
      }
      return `/reports/space-usage?${queryParams}`;
    },
    errorMessage: "Unable to view usage on this invoice",
  },
  viewServiceCharge: {
    endpoint: (item: InvoiceTransactionVO) => `/buyer/service-fee/${item.transactionId}`,
    errorMessage: "Unable to view service charge for this invoice",
  }
} satisfies Record<string, ActionPayload<InvoiceTransactionVO>>;

/**
 * Component that displays all information for a single buyer invoice item
 * @param item the InvoiceTransactionVO to display
 * @param expanded
 * @param onToggleExpand
 * @constructor
 */

const BuyerInvoicesCardListItem = ({
  item,
  expanded,
  onToggleExpand
}: BuyerInvoicesCardListItemProps) => {

  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();
  const { snackbarShowMessage } = useSnackbar();

  const actionCallback: OnActionCallback = (action, _instrument, message) => {
    if (action === CallbackActions.ERROR && message) {
      snackbarShowMessage(message, 'error');
    }
    handleClose();
  };

  return (
    <CollapsableCardContainer expanded={expanded}>
      <CollapsableCardClickableContainer onClick={onToggleExpand}>
        <CollapsableCardHeaderContainer>
          <CollapsableCardHeaderItem value={item?.transactionNumber} label={'Invoice Number'} />
          <CollapsableCardHeaderItem value={formatCurrencyValue(item?.buyerAmount, true)} label={'Amount'} />
          <CollapsableCardHeaderItem value={(BookingTransactionTypeLabel as Record<string, string>)[String(item?.transactionType)] ?? item?.transactionType} label={'Invoice Type'} />
          <CollapsableCardHeaderItem value={item?.serviceDates} label={'Service Dates'} />
          <CollapsableCardHeaderItem value={item?.bookingNumber} label={'Booking'} />
          <CollapsableCardHeaderItem value={item?.assetType} label={'Equipment Type'} />
          <CollapsableCardHeaderActionsContainer>
            <CollapsableCardExpandIconButton expanded={expanded ?? false} />
          </CollapsableCardHeaderActionsContainer>
        </CollapsableCardHeaderContainer>
      </CollapsableCardClickableContainer>
      <CollapsableCardCollapseContainer in={expanded}>
        <CollapsableCardBodyContainer>
          <CollapsableCardBodyItem value={item?.paymentMethodDescription} label={'Payment Method'} />
          <CollapsableCardBodyItem value={item?.paymentType} label={'Payment Type'} />
          <CollapsableCardBodyItem value={item?.numberOfSpaces} label={'Spaces'} />
          <CollapsableCardBodyItem value={item?.createdOn} label={'Invoice Created Date'} />
          <CollapsableCardBodyItem value={item?.paymentCreatedOn} label={'Payment Initiated Date'} />
          <CollapsableCardBodyItem value={item?.paymentCompletedOn} label={'Process Completed Date'} />

          <CollapsableCardBodyItem value={item?.locationName} label={'Location'} />
          <CollapsableCardBodyItem value={item?.supplierCompanyName} label={'Supplier'} />
          <CollapsableCardBodyItem value={getServiceFeeDisplayValue(item?.serviceFeeType)} label={'Service Charge Type'} />
          <CollapsableCardBodyItem value={BookingTransactionStatus[item?.status]?.buyerInvoiceStatusGroup?.label ?? item?.status} label={'Status'} />

          <CollapsableCardBodyActionsContainer>
            <ActionsMenuOpenButton onClick={handleClick}/>
            <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
              <ActionItem
                label="View Invoice"
                icon={ActionDefaultIcons[ActionTypes.DOWNLOAD]}
                enable={Boolean(item?.transactionNumber)}
                type={ActionTypes.OPEN_TAB}
                instrument={item}
                actionPayload={openTabActionPayloads.viewInvoice}
                onAction={actionCallback}
              />
              <ActionItem
                label="View Usage"
                icon={ActionDefaultIcons[ActionTypes.HANDLER]}
                enable={Boolean(item?.transactionNumber)}
                type={ActionTypes.OPEN_TAB}
                instrument={item}
                actionPayload={openTabActionPayloads.viewUsage}
                onAction={actionCallback}
              />
              <ActionItem
                label="Approve/Decline Invoice"
                icon={ActionDefaultIcons[ActionTypes.POST_REQUEST]}
                show={item.transactionType === BookingTransactionType.SERVICE_CHARGE}
                type={ActionTypes.OPEN_TAB}
                instrument={item}
                actionPayload={openTabActionPayloads.viewServiceCharge}
                onAction={actionCallback}
              />
            </ActionsMenu>
          </CollapsableCardBodyActionsContainer>

        </CollapsableCardBodyContainer>
      </CollapsableCardCollapseContainer>
    </CollapsableCardContainer>
  );
};

export { BuyerInvoicesCardListItem };
